import { useState, useRef, useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { Logo } from "./Logo";
import { MobileBottomNav, PressEyebrow, PressLogo } from "@/components/pullpress/PressVisuals";

type Variant = "public" | "auth" | "minimal" | "operator";

export function AppShell({ variant = "auth", children }: { variant?: Variant; children: ReactNode }) {
  const showMobileNav = variant === "auth";
  const showFooter = variant !== "minimal";
  return (
    <div className={`relative flex min-h-[100dvh] flex-col bg-[radial-gradient(circle_at_50%_-10%,var(--as-paper-raised)_0_18rem,transparent_34rem),linear-gradient(180deg,var(--as-margin)_0%,var(--as-paper)_56%,var(--as-paper-2)_100%)] text-ink-1 ${showMobileNav ? "pb-20 md:pb-0" : ""}`}>
      <Header variant={variant} />
      <main className="relative z-10 flex-1">{children}</main>
      {showFooter && <WalnutRule />}
      {showFooter && <Footer variant={variant} />}
      {showMobileNav && <MobileBottomNav />}
    </div>
  );
}

function WalnutRule() {
  return <div className="relative z-10 h-px w-full bg-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)]" />;
}

function Footer({ variant }: { variant: Variant }) {
  return (
    <footer className="relative z-10 px-4 py-5 sm:px-6 lg:px-10">
      <div className="flex flex-col items-center justify-between gap-3 border-t border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] pt-5 md:flex-row">
        <PressEyebrow>PullPress makes books easier to finish.</PressEyebrow>
        <div className="flex flex-wrap items-center justify-center gap-6">
          {variant === "public" && (
            <>
              <FooterLink to="/">Product</FooterLink>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterLink to="/login">Log in</FooterLink>
            </>
          )}
          <Link to="/support" className="inline-flex min-h-11 items-center font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3 hover:text-walnut-deep">
            Support
          </Link>
          <PressEyebrow>© 2026 PullPress</PressEyebrow>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="inline-flex min-h-11 items-center font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3 hover:text-walnut-deep">
      {children}
    </Link>
  );
}

const NAV_SHELL =
  "sticky top-0 z-30 flex min-h-16 items-center border-b border-[color:color-mix(in_oklab,var(--as-rule)_38%,transparent)] bg-paper-raised/70 px-4 py-2 backdrop-blur-2xl sm:px-6 lg:px-10";

function Header({ variant }: { variant: Variant }) {
  if (variant === "minimal") {
    return (
      <nav className={`${NAV_SHELL} justify-center`}>
        <Link to="/" aria-label="Home" className="inline-flex min-h-11 items-center"><Logo /></Link>
      </nav>
    );
  }

  if (variant === "public") {
    return (
      <nav className={`${NAV_SHELL} grid grid-cols-[1fr_auto] gap-4 sm:grid-cols-[auto_1fr] lg:grid-cols-[1fr_auto_1fr]`}>
        <div className="hidden min-w-0 lg:block">
          <p className="max-w-[300px] truncate font-ui text-sm text-ink-2">
            Finish the book.
          </p>
        </div>
        <Link to="/" aria-label="Home" className="inline-flex min-h-11 items-center justify-self-start lg:justify-self-center">
          <Logo size="md" />
        </Link>
        <div className="flex items-center justify-self-end gap-3">
          <Link to="/login" className="hidden min-h-11 items-center font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-2 transition-colors duration-as-1 hover:text-walnut-deep sm:inline-flex">
            Sign in
          </Link>
          <Link
            to="/signup?package=self-serve"
            className="hidden min-h-11 items-center justify-center rounded-as-2 bg-walnut px-[14px] py-[7px] font-ui text-t-13 font-semibold text-paper-raised shadow-as-1 transition-colors duration-as-1 hover:bg-walnut-deep sm:inline-flex"
          >
            Start my book
          </Link>
        </div>
      </nav>
    );
  }

  if (variant === "operator") {
    return (
      <nav className={`${NAV_SHELL} justify-between gap-3 lg:pl-24`}>
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <Link to="/" aria-label="Home" className="inline-flex min-h-11 items-center"><PressLogo /></Link>
          <span className="hidden rounded-as-1 bg-ink-1 px-[8px] py-[3px] font-mono text-[11px] uppercase tracking-as-eyebrow text-paper-raised sm:inline-flex">
            Operator
          </span>
        </div>
        <NavLink to="/dashboard">Author view</NavLink>
      </nav>
    );
  }

  // auth (default)
  return (
    <nav className={`${NAV_SHELL} justify-between`}>
      <div className="hidden lg:block">
        <p className="font-ui text-sm text-ink-2">Keep the book moving.</p>
      </div>
      <Link to="/dashboard" aria-label="Home" className="inline-flex min-h-11 items-center"><Logo /></Link>
      <div className="flex items-center gap-5">
        <AuthNav />
        <UserMenu />
      </div>
    </nav>
  );
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link
      to={to}
      className={
        "inline-flex min-h-11 items-center font-mono text-[11px] tracking-as-eyebrow uppercase transition-colors duration-as-1 " +
        (active ? "text-walnut-deep" : "text-ink-2 hover:text-walnut-deep")
      }
    >
      {children}
    </Link>
  );
}

function AuthNav() {
  const links = [
    { to: "/dashboard", label: "Books" },
    { to: "/new-project", label: "Start" },
  ];
  return (
    <div className="hidden md:flex gap-5">
      {links.map((l) => (
        <NavLink key={l.to} to={l.to}>{l.label}</NavLink>
      ))}
    </div>
  );
}

function UserMenu() {
  const { user, signOut } = useAuthContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initial = (user?.displayName ?? user?.email ?? "U").charAt(0).toUpperCase();

  return (
    <div ref={ref} className="relative border-l border-rule pl-5">
      <button
        onClick={() => setOpen(!open)}
        className="flex h-11 w-11 items-center justify-center rounded-as-2 bg-ink-1 transition-colors duration-as-1 hover:bg-walnut-deep"
        aria-label="Open author menu"
      >
        <span className="font-mono text-[11px] font-semibold uppercase text-paper-raised">
          {initial}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/95 p-2 shadow-as-3 backdrop-blur-2xl">
          <div className="px-3 py-2">
            <p className="font-display font-semibold text-t-16 text-ink-1">
              {user?.displayName ?? "Author"}
            </p>
            <p className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3 mt-[2px]">
              {user?.email}
            </p>
          </div>
          <div className="my-1 h-px bg-rule" />
          <button
            onClick={() => { signOut(); setOpen(false); }}
            className="w-full text-left px-3 py-2 font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3 hover:text-walnut-deep transition-colors duration-as-1"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
